import { BASE_URL } from "@utils/constants";
import {
  addAssignedProjects,
  addAssignedFolders,
  addAssignedFiles,
  setSelectedAssignedProjectId,
  setSelectedAssignedFolderId,
  setSelectedAssignedFileId,
} from "@store/slices/validatorAdminSlice";
import apiClient from "@/services/api/apiClient";

// Fetch projects for validator admin
export const fetchValidatorAdminProjects = (keyword = '', page = 0, size = 10) => async (dispatch, getState) => {
  try {
    const state = getState();
    const  userId = state.auth.userId;
    const data = await apiClient(
      BASE_URL + `api/validatorAdmin/projects?validatorAdminId=${userId}&searchKeyword=${keyword}&page=${page}&size=${size}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${state.auth.token}`
        }
      }
    );
    console.log("Fetched validator admin projects:", data.data);
    dispatch(addAssignedProjects(data.data));
    if (data.data?.length) {
      console.log(data.data[0].projectId)
      dispatch(setSelectedAssignedProjectId(data.data[0].projectId));
    }
    return data.data;
  } catch (error) {
    console.error("Error fetching validator admin projects:", error);
  }
};

// Fetch folders for validator admin and project
export const fetchValidatorAdminFolders = (projectId, keyword = '', page = 0, size = 10) => async (dispatch, getState) => {
  try {
    // Ensure project ID is valid
    if (projectId === undefined || projectId === null || projectId === "undefined") {
      console.error("Invalid project ID:", projectId);
      return [];
    }

    const state = getState();
    const { userId, token } = state.auth;

    // Convert to number if it's a numeric string
    const projectIdToUse = !isNaN(Number(projectId)) ? Number(projectId) : projectId;

    // Ensure BASE_URL ends with a slash
    const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;
    const url = `${baseUrl}api/validatorAdmin/folders?projectId=${projectId}&validatorAdminId=${userId}&searchKeyword=${keyword}&page=${page}&size=${size}`;


    const data = await apiClient(url,
      {
        method: "GET",
       credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });
    console.log("Fetched validator admin folders for project", projectIdToUse, ":", data.data);

    // Process folders data to ensure we have the complete structure
    let folders = Array.isArray(data.data) ? data.data : [];

    // Ensure all folder IDs are numeric
    folders = folders.map(folder => {
      // Convert IDs to numbers
      const folderDetailsId = folder.folderDetailsId !== undefined ?
        (!isNaN(Number(folder.folderDetailsId)) ? Number(folder.folderDetailsId) : folder.folderDetailsId) :
        null;

      const folderId = folder.folderId !== undefined ?
        (!isNaN(Number(folder.folderId)) ? Number(folder.folderId) : folder.folderId) :
        folderDetailsId;

      // If the folder already has fileDetails, keep them
      const fileDetails = folder.fileDetails && Array.isArray(folder.fileDetails)
        ? folder.fileDetails
        : [];

      return {
        ...folder,
        folderDetailsId,
        folderId,
        fileDetails
      };
    });


    dispatch(addAssignedFolders({ projectId: projectIdToUse, folders }));
    if (folders?.length) {
      const firstFolder = folders[0];
      const firstFolderId = firstFolder.folderDetailsId || firstFolder.folderId;

      if (firstFolderId !== undefined && firstFolderId !== null) {
        console.log("Setting selected folder ID to:", firstFolderId, "type:", typeof firstFolderId);
        dispatch(setSelectedAssignedFolderId(firstFolderId));

        // If the folder has fileDetails, also update the files in the store
        if (firstFolder.fileDetails && Array.isArray(firstFolder.fileDetails)) {
          dispatch(addAssignedFiles({
            folderId: firstFolderId,
            files: firstFolder.fileDetails
          }));
        }
      } else {
        console.error("First folder has invalid ID:", firstFolder);
      }
    }
    return folders;
  } catch (error) {
    console.error("Error fetching validator admin folders:", error);
    return [];
  }
};

// Fetch files for validator admin and folder
export const fetchValidatorAdminFiles = (folderId, keyword = '', page = 0, size = 10) => async (dispatch, getState) => {
  try {
    // Ensure folder ID is valid - prevent sending "undefined" as a string
    if (folderId === undefined || folderId === null || folderId === "undefined") {
      console.error("Invalid folder ID:", folderId);
      return [];
    }

    const state = getState();
    const { userId, token } = state.auth;

    // Convert to number if it's a numeric string
    const folderIdToUse = !isNaN(Number(folderId)) ? Number(folderId) : folderId;

    console.log(`Fetching files for folder ${folderIdToUse} (${typeof folderIdToUse}) and validator admin ${userId}`);

    // Ensure BASE_URL ends with a slash
    const baseUrl = BASE_URL.endsWith('/') ? BASE_URL : `${BASE_URL}/`;

    // Only proceed if we have a valid numeric folder ID
    if (typeof folderIdToUse !== 'number') {
      console.error("Folder ID must be a number, got:", folderIdToUse);
      return [];
    }

    const url = `${baseUrl}api/validatorAdmin/files?folderId=${folderId}&validatorAdminId=${userId}&searchKeyword=${keyword}&page=${page}&size=${size}`;

    console.log("Calling API with URL:", url);

    const data = await apiClient(url,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      });


    console.log("Fetched validator admin files for folder", folderIdToUse, ":", data.data);

    if (!data) {
      console.error("Failed to fetch files from all endpoints");
      dispatch(addAssignedFiles({ folderId: folderIdToUse, files: [] }));
      return [];
    }

    // Process the data based on the structure
    let files = [];

    if (Array.isArray(data.data)) {
      if (data.data.length > 0 && data.data[0].fileDetails && Array.isArray(data.data[0].fileDetails)) {
        // This is the structure from your console output: array with object containing fileDetails
        files = data.data[0].fileDetails;
        console.log("Extracted files from data.data[0].fileDetails:", files);
      } else {
        // Direct array of files
        files = data.data;
        console.log("Using direct array of files:", files);
      }
    } else if (data.data && typeof data === 'object') {
      if (data.data.fileDetails && Array.isArray(data.data.fileDetails)) {
        // Handle the specific response structure: {folderDetailsId, folderName, fileDetails, created_at}
        files = data.data.fileDetails;
        console.log("Extracted files from data.fileDetails:", files);

        // If we have folderName, we could update it in the store if needed
        if (data.data.folderName) {
          console.log("Folder name from response:", data.data.folderName);
        }
      }
    }

    // Update Redux store
    console.log("Dispatching files to Redux store for folder ID:", folderIdToUse, "with files:", files);
    dispatch(addAssignedFiles({ folderId: folderIdToUse, files }));

    // Return the files
    return data.data; // Return the full data object for more flexibility
  } catch (error) {
    console.error("Error fetching validator admin files:", error);
    dispatch(addAssignedFiles({ folderId: folderId || 0, files: [] }));
    return [];
  }
};

export const fetchFileAssignmentByFileId = (fileId) => async (dispatch, getState) => {
  const state = getState();
  const { userId, token } = state.auth;

  try {
    const data = await apiClient(
      BASE_URL + `api/validatorAdmin/filesByFileId?fileId=${fileId}&validatorAdminId=${userId}`,
      {
        method: "GET",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        }
      }
    );
    return data.data;
  } catch (error) {
    console.error("Error fetching file assignment by fileId:", error);
    return [];
  }
};
